import { Dialog, DialogPanel, Listbox, ListboxOption, ListboxOptions, Transition, TransitionChild } from '@headlessui/react';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { Fragment, useEffect, useState } from 'react';
import { Token } from '~/lib/types';
import { classNames, getIconUrl, onIconError, prettyValue } from '~/lib/util';
import { filterTokensByTab, TOKEN_TABS } from "~/lib/token-tabs";

type Props = {
	name: string;
	tokens: Token[];
	balances?: any[];
	selected: Token;
	onSelected: (value: Token) => void;
};

const tabs = TOKEN_TABS;

const MAX_DEFAULT_TOKEN_COUNT = 20;

export default function TokenSelector({ name, tokens, balances, selected, onSelected }: Props) {
	const [displayTokens, setDisplayTokens] = useState<Token[]>([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [query, setQuery] = useState("");
	const [open, setOpen] = useState(false);
	const [activeTab, setActiveTab] = useState(tabs[0]);

	function onSearch(term: string) {
		setSearchTerm(term);
	}

	function sortTokens(a: Token, b: Token) {
		if (!balances) {
			return 0;
		}

		const balanceA = parseFloat(balances.find((bal) => bal.address == a.address)?.amount || 0);
		const balanceB = parseFloat(balances.find((bal) => bal.address == b.address)?.amount || 0);

		const priceA = balanceA * a.tokenPriceUSD;
		const priceB = balanceB * b.tokenPriceUSD;

		if (priceA < priceB) {
			return 1;
		} else if (priceA > priceB) {
			return -1;
		} else if (balanceA < balanceB) {
			return 1;
		} else if (balanceA > balanceB) {
			return -1;
		} else if (a.tvl < b.tvl) {
			return 1;
		} else if (a.tvl > b.tvl) {
			return -1;
		}

		return 0;
	}

	useEffect(() => {
		const tabbedTokens = filterTokensByTab(tokens, activeTab, balances);
		tabbedTokens.sort(sortTokens);

		setDisplayTokens(tabbedTokens);
	}, [activeTab, tokens, balances]);

	// useEffect(() => {
	// 	if (!balances) {
	// 		return;
	// 	}

	// 	const copyTokens = [...tokens];

	// 	copyTokens.sort(sortTokens);

	// 	setDisplayTokens(copyTokens.slice(0, MAX_DEFAULT_TOKEN_COUNT));
	// }, [balances]);

	useEffect(() => {
		if (!open) {
			setQuery("");
		}
	}, [open]);

	useEffect(() => {
		const regExSearch = new RegExp(searchTerm, "i");

		// Reset search and display all tokens
		if (!searchTerm) {
			const sortedTokens = [...tokens];
			sortedTokens.sort(sortTokens);
			setDisplayTokens(sortedTokens.slice(0, MAX_DEFAULT_TOKEN_COUNT));
			return;
		}

		// Filter tokens by search term
		const filteredTokens = [];
		for (const token of tokens) {
			if (token.symbol && token.symbol.match(regExSearch)) {
				filteredTokens.push(token);
				continue;
			}

			if (token.name && token.name.match(regExSearch)) {
				filteredTokens.push(token);
			}
		}

		filteredTokens.sort(sortTokens);
		setDisplayTokens(filteredTokens);
	}, [searchTerm]);

	return (
		<>
			<div className="flex w-40 flex-none justify-end">
				<Listbox as="div" name={name} value={selected} onChange={onSelected}>
					<>
						<div className="">
							<Listbox.Button onClick={() => setOpen(true)} className="relative w-full text-black dark:text-[#e8e8ee] hover:text-primary-400 dark:hover:text-primary-300 rounded-md pr-10 py-2 text-left focus:outline-3 focus:ring-1 focus:ring-primary-300/50 sm:text-sm">
                        <span className="flex items-center">
                           <img src={getIconUrl(selected.iconUrl)} alt="" className="shrink-0 h-8 w-8 rounded-full" />
                           <span className="ml-3 block text-xl font-medium">{selected.symbol}</span>
                           {/* <span className="ml-3 text-xs text-gray-500 truncate">{selected.name}</span> */}
                        </span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center">
                           <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                     </Listbox.Button>

							<Transition show={open} as={Fragment} afterLeave={() => setQuery("")} appear>
								<Dialog as="div" className="relative z-1001" onClose={setOpen}>
									<TransitionChild
										as={Fragment}
										enter="ease-out duration-300"
										enterFrom="opacity-0"
										enterTo="opacity-100"
										leave="ease-in duration-200"
										leaveFrom="opacity-100"
										leaveTo="opacity-0"
									>
										<div className="_backdrop-blur-lg fixed inset-0 bg-black/50 transition-opacity" />
									</TransitionChild>

									<div className="fixed inset-0 h-screen overflow-y-auto">
										<TransitionChild
											as={Fragment}
											enter="ease-out duration-300"
											enterFrom="opacity-0 scale-95"
											enterTo="opacity-100 scale-100"
											leave="ease-in duration-200"
											leaveFrom="opacity-100 scale-100"
											leaveTo="opacity-0 scale-95"
										>
											<DialogPanel className="divide-opacity-10 ring-opacity-5 transform divide-y divide-gray-200 overflow-hidden bg-white text-black transition-all sm:rounded-lg sm:shadow-lg md:mx-auto md:mt-12 md:max-w-md">
												<div className="relative border-white max-sm:m-2 max-sm:rounded-lg max-sm:border">
													<MagnifyingGlassIcon className="pointer-events-none absolute top-2.5 left-4 h-5 w-5 text-black/40" aria-hidden="true" />
													<input
														type="search"
														value={searchTerm}
														onChange={(evt) => onSearch(evt.target.value)}
														autoFocus
														autoCorrect="false"
														onClick={(evt) => evt.stopPropagation()}
														onFocus={(evt) => evt.stopPropagation()}
														placeholder="Search..."
														className="h-10 w-full border-none bg-transparent pr-3 pl-12 placeholder-neutral-400"
													/>
												</div>
												<div>
													<div className="overflow-x-scroll p-2">
														<nav aria-label="Tabs" className="flex space-x-4">
															{tabs.map((tab) => (
																<button
																	key={tab.name}
																	onClick={() => setActiveTab(tab)}
																	aria-current={activeTab == tab ? "page" : undefined}
																	className={classNames(
																		activeTab == tab ? "bg-gray-100 text-gray-700" : "text-gray-500 hover:text-gray-700",
																		"rounded-md px-3 py-2 text-sm font-medium",
																	)}
																>
																	{tab.name}
																</button>
															))}
														</nav>
													</div>
												</div>
												<ListboxOptions static className="divide-opacity-50 max-h-max scroll-py-2 divide-y divide-neutral-500 overflow-y-auto md:max-h-[30rem]">
													{/* <li className="grid grid-cols-2 gap-2 p-2 md:grid-cols-4">
														{}
													</li> */}

													<div className="p-2">
														{displayTokens.map((token) => {
															let tokenBalance = "";
															let tokenPrice = "";

															if (balances) {
																const balance = balances.find((b) => b.address == token.address);

																if (balance) {
																	tokenBalance = prettyValue(balance.amount, false);
																	tokenPrice = (Math.round(balance.amount * token.tokenPriceUSD * 100) / 100).toLocaleString();
																}
															}

															return (
																<ListboxOption
																	onClick={() => setOpen(false)}
																	key={token.address}
																	value={token}
																	className="relative cursor-default py-2 pr-9 pl-3 select-none sm:hover:bg-neutral-200"
																>
																	{({ selected }) => (
																		<>
																			<div className="flex items-center justify-between">
																				<div className="flex items-center justify-between">
																					<img src={getIconUrl(token.iconUrl, 24)} onError={(evt) => onIconError(evt)} alt="" className="h-6 w-6 shrink-0 rounded-full" />
																					<div className={classNames(selected ? "font-semibold" : "font-normal", "ml-3 block truncate")}>
																						<p className="font-optical-24-550 tracking-astro flex items-center space-x-1 text-sm">
																							<span>{token.name || "Unknown"}</span>
																						</p>
																					</div>
																				</div>
																				<div className="name font-optical-24-550 tracking-astro text-right font-mono text-xs sm:text-black/50">
																					<p>
																						{tokenBalance} {token.symbol}
																					</p>
																					{tokenPrice ? <p>${tokenPrice}</p> : null}
																				</div>
																			</div>
																		</>
																	)}
																</ListboxOption>
															);
														})}
													</div>
												</ListboxOptions>
											</DialogPanel>
										</TransitionChild>
									</div>
								</Dialog>
							</Transition>
						</div>
					</>
				</Listbox>
			</div>
		</>
	);
}
